<template>
  <ErrorBoundary v-if="isMounted">
    <div class="fe-w-full fe-spacing" v-if="selectedLayoutType">
      <LayoutSelector
        :layout-type="selectedLayoutType"
        :binding-type="bindingType"
      />
      <ModalRegistry />
      <ToastRegistry />
    </div>
  </ErrorBoundary>
</template>

<script>
  /** Components */
  import LayoutSelector from "@/layouts/RnRLayouts/LayoutSelector.vue"
  import ModalRegistry from "@/components/Utilities/ModalRegistry/ModalRegistry.vue"
  import ToastRegistry from "@/components/Utilities/ToastRegistry/ToastRegistry.vue"
  import ErrorBoundary from "@/components/Utilities/ErrorBoundary/ErrorBoundary.vue"

  /** Other Items */
  import {
    apiGetProductReviews,
    apiGetProductStats,
    apiGetUserReview,
    apiGetProductReviewImages,
  } from "@/api_service/customerApi"
  import { apiGetWidgetConfig } from "@/api_service/configApi"
  import {
    isEmpty,
    checkIsPlatform,
    getDerivedProductValues,
  } from "@/utils/utils"
  import { BINDING_TYPE, CURRENT_VERTICAL } from "@/constants/constants"

  const initialState = {
    // Although we have util method to identify current vertical, but in case if we ever need it in state also, so keeping it here.
    currentVertical: CURRENT_VERTICAL,
    // review
    reviews: [],
    reviewsWithImages: [],
    avgRating: null,
    ratingsCountDetails: null,
    reviewsCountDetails: null,
    variantDetails: null,
    ratingsCount: 0,
    reviewsCount: 0,
    verifiedCount: 0,
    imageCount: 0,
    userCurrentReview: null,
    userReviewed: false,
    isPendingReview: false,
    rating: 0,
    reviewsPageMeta: {
      loading: false,
      current: 0,
      size: 4,
      hasNext: false,
      sortBy: "MOST_RECENT",
    },
    reviewsWithImagesPageMeta: {
      loading: false,
      current: 0,
      size: 30,
      hasNext: false,
      sortBy: "MOST_RECENT",
    },

    // All the derived product values
    product: {},

    // ALl required user data
    user: {},

    // config related to RnR (Either will be fetched from api or sent as prop from Fynd Platform)
    widgetConfig: null,
  }

  export default {
    name: "ReviewAndRatingWidget",
    components: {
      LayoutSelector,
      ModalRegistry,
      ToastRegistry,
      ErrorBoundary,
    },
    props: {
      config: {
        // This prop is used only when this component is running inside Platform Preview
        type: Object,
        default: () => ({}),
        required: false,
      },
      //
      bindingType: {
        type: String,
        default: BINDING_TYPE.REVIEW_AND_RATING,
      },
    },
    provide() {
      return {
        globalState: this.state,
        resetAndInitializeApi: this.initializeAPIs,
        getProductReviews: this.getProductReviews,
        getProductReviewImages: this.getProductReviewImages,
      }
    },
    data() {
      return {
        state: JSON.parse(JSON.stringify(initialState)),
        isFirstMounted: true,
        isMounted: false,
      }
    },
    watch: {
      config() {
        if (this.isPlatform) {
          this.state.widgetConfig = this.config.data
          return
        }
      },
    },
    computed: {
      selectedLayoutType() {
        return this.state.widgetConfig?.layout?.selectedLayout || ""
      },
      isPlatform() {
        return checkIsPlatform()
      },
    },
    mounted() {
      this.isMounted = true

      this.subscribeToFpiProductState("subscribe")
      this.subscribeToFpiUserState("subscribe")
    },
    destroyed() {
      this.subscribeToFpiProductState("unsubscribe")
      this.subscribeToFpiUserState("unsubscribe")
    },
    methods: {
      subscribeToFpiProductState(type) {
        if (type === "subscribe") {
          window.FPI?.state?.product?.subscribe(
            this.setProductDataAndInitializeAPIs.bind(this),
          )
        }

        if (type === "unsubscribe") {
          window.FPI?.state?.product?.unsubscribe(
            this.setProductDataAndInitializeAPIs.bind(this),
          )
        }
      },

      subscribeToFpiUserState(type) {
        if (type === "subscribe") {
          window.FPI?.state?.user?.subscribe(this.setUserData.bind(this))
        }

        if (type === "unsubscribe") {
          window.FPI?.state?.user?.unsubscribe(this.setUserData.bind(this))
        }
      },

      setUserData(user) {
        const userData = user?.user
        this.state.user = {
          user_id: userData?.user_id,
        }
      },

      setProductDataAndInitializeAPIs(product) {
        const productData = getDerivedProductValues(product?.product)

        const currentProductId =
          this.state.product?.productId || window.rnr_product_id || ""
        // User has not moved to new product, so no need to update the data
        if (currentProductId === productData?.productId) {
          return
        }

        // Set derived product data in product object
        this.state.product = productData

        this.initializeAPIs("reset_state")
      },

      initializeAPIs(shouldResetState) {
        // this.resetComponentData()
        this.getWidgetConfig()
        this.getProductReviewImages(0, shouldResetState)
        this.getUserReview()
        this.getProductSummary()
        this.getProductReviews(0, false, shouldResetState)
      },

      // resetComponentData() {
      //   // No need to reset the state on the first run
      //   if (!this.isFirstMounted) {
      //     this.isFirstRun = false

      //     // We should not reset the product Data, except that, we will reset everything back to initial state
      //     this.state = {
      //       ...initialState,
      //       product: this.state?.product || {},
      //     }
      //   }
      // },

      async getWidgetConfig() {
        // If component is running on Fynd Platform, Then no need to fetch config, because it will be coming from Fynd Platform as prop
        if (this.isPlatform) {
          return
        }

        try {
          const configToFetch = {
            configIdentifierList: [
              { type: "vertical_config", subtype: "layout" },
              { type: "vertical_config", subtype: "review_properties" },
            ],
          }

          const response = await apiGetWidgetConfig(configToFetch)

          // if (!isEmpty(response.data)) {
          //TODO - Need to write comment of the response structure
          // Converting Array of object to Object with key - Subtype
          const widgetConfig = response?.data?.reduce((config, item) => {
            config[item.subtype] = item.data
            return config
          }, {})

          this.state.widgetConfig = widgetConfig || null

          return Promise.resolve()
          // }
        } catch (error) {
          console.log(error.message)
        }
      },

      async getUserReview() {
        try {
          const productId = this.state.product?.productId

          const response = await apiGetUserReview(productId)

          // if (!isEmpty(response.data)) {
          const { data = {} } = response

          if (response.data) {
            const isPendingReview = this.checkIfReviewInPendingState(data)

            this.state.userCurrentReview = isPendingReview
              ? data["PENDING"]
              : data["APPROVED"]
            this.state.userReviewed = true
            this.state.isPendingReview = isPendingReview
          } else {
            this.state.userCurrentReview = null
            this.state.userReviewed = false
            this.state.isPendingReview = false
          }

          return Promise.resolve()
          // }
        } catch (error) {
          console.log("get user review", error)
          this.state.rating = 0
        }
      },

      async getProductSummary() {
        try {
          const response = await apiGetProductStats(
            this.state.product.productId,
          )

          // if (!isEmpty(response.data)) {
          const { data = {} } = response

          this.state.avgRating = data.averageRating || null
          this.state.ratingsCountDetails = data.ratingsCountDetails || null
          this.state.ratingsCount = data.ratingsCount || 0
          this.state.reviewsCount = data.reviewsCount || 0

          this.state.reviewsCountDetails = data.reviewsCountDetails || null
          this.state.variantDetails =
            data.variantDetails?.reduce((vmap, obj) => {
              vmap[obj.alternateCode2] = obj
              return vmap
            }, {}) || null
          this.state.verifiedCount = data.verifiedCount || 0
          this.state.imageCount = data.imageCount || 0
          return Promise.resolve()
          // }
        } catch (error) {
          console.log("get product summary", error)
        }
      },

      async getProductReviews(
        pageNoToBeFetched = 0,
        pagination = false,
        shouldResetState,
      ) {
        if (this.state.reviewsPageMeta?.loading) {
          return
        }

        try {
          this.state.reviewsPageMeta.loading = true

          const query = {
            pageNo: pageNoToBeFetched,
            pageSize: this.state.reviewsPageMeta?.size,
            sortBy: "MOST_RECENT",
          }

          const response = await apiGetProductReviews(
            this.state.product.productId,
            query,
            shouldResetState,
          )

          if (!isEmpty(response.data)) {
            const { data } = response

            if (data.reviews?.length) {
              if (pagination || shouldResetState === "reset_state") {
                this.state.reviews = data.reviews
              } else {
                this.state.reviews = [...this.state.reviews, ...data.reviews]
              }
            } else {
              this.state.reviews = data.reviews
            }

            this.state.reviewsPageMeta = {
              ...this.state.reviewsPageMeta,
              ...data?.page,
              loading: false,
            }
            // this.state.reviewsPageMeta.loading = false
          } else {
            this.state = {
              ...this.state,
              reviews: [],
            }
            this.state.reviewsPageMeta = {
              ...initialState.reviewsPageMeta,
            }
          }
          return Promise.resolve()
        } catch (error) {
          this.state.reviewsPageMeta.loading = false
          console.log("get product review", error)
        }
      },

      async getProductReviewImages(pageNoToBeFetched = 0, shouldResetState) {
        if (this.state.reviewsWithImagesPageMeta?.loading) {
          return
        }

        try {
          this.state.reviewsWithImagesPageMeta.loading = true

          const query = {
            productId: this.state.product.productId,
            pageNo: pageNoToBeFetched,
            pageSize: this.state.reviewsWithImagesPageMeta?.size,
            sortBy: "MOST_RECENT",
          }

          const response = await apiGetProductReviewImages(query)

          const { data = {} } = response

          if (data.reviews?.length) {
            // In this api, we are getting imageList key for all review images, but in all other api, we are getting images key.
            // So just to keep the consistency, we are updating the key from imageList to images
            // In future, if backend fix this, then we should remove this operation
            const updatedReviews = data.reviews.map((review) => {
              const { imageList, ...reviewObj } = review

              return {
                ...reviewObj,
                images: imageList,
              }
            })

            if (shouldResetState) {
              this.state.reviewsWithImages = updatedReviews
            } else {
              this.state.reviewsWithImages = [
                ...this.state.reviewsWithImages,
                ...updatedReviews,
              ]
            }

            this.state.reviewsWithImagesPageMeta = {
              ...this.state.reviewsWithImagesPageMeta,
              ...data?.page,
            }
            this.state.reviewsWithImagesPageMeta.loading = false
          } else {
            this.state.reviewsWithImages = []
            this.state.reviewsWithImagesPageMeta = {
              ...initialState.reviewsWithImagesPageMeta,
            }
          }
          return Promise.resolve()
        } catch (error) {
          this.state.reviewsWithImagesPageMeta.loading = false
          console.log("get review image", error)
        }
      },

      checkIfReviewInPendingState(reviewDataArray) {
        return !isEmpty(reviewDataArray["PENDING"])
      },
    },
  }
</script>

<style lang="scss">
  @import "../../assets/styles/styles.scss";
</style>
